import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useEmailTemplates } from '#emailCampaignFeature/models/emailTemplates/queries';
import { useErrorStore } from '#lib/stores/errorStore';
import { usePostMailingTemplates } from '#postMailingFeature/models/postMailingTemplates/queries';

import { listDeals, listCompanies } from '../../../api/crmApi';
import { useCreateActivity } from '../../../models/activities/mutations';
import { useChannelTypes } from '../../../models/channels/queries';
import { useCompanyDetail } from '../../../models/companies/queries';

function useCompanySearch(query) {
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!query || query.trim().length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const res = await listCompanies({ search: query.trim(), pageSize: 6 });
				if (!cancelled) setResults(res.companies ?? []);
			} catch {
				/* ignore */
			}
			if (!cancelled) setLoading(false);
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	return { results, loading };
}

function useDealSearch(query, companyId) {
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!query || query.trim().length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const filters = { search: query.trim(), pageSize: 6 };
				if (companyId) filters.crmCompanyId = companyId;
				const res = await listDeals(filters);
				if (!cancelled) setResults(res.deals ?? []);
			} catch {
				/* ignore */
			}
			if (!cancelled) setLoading(false);
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query, companyId]);

	return { results, loading };
}

function buildNotePayload(base, body) {
	const now = new Date().toISOString();
	return {
		...base,
		activityType: 'note',
		title: 'Notiz',
		body,
		status: 'completed',
		completedAt: now,
	};
}

function buildLogPayload(base, { isCustomChannel, channelTypeId, customLabel, title, body }) {
	const now = new Date().toISOString();
	return {
		...base,
		activityType: isCustomChannel ? 'meeting' : 'communication',
		channelTypeId: channelTypeId || null,
		customActivityLabel: isCustomChannel ? customLabel || null : null,
		title: title.trim(),
		body: body || null,
		status: 'completed',
		completedAt: now,
		channelPayload: { direction: 'outbound' },
	};
}

function buildTodoPayload(
	base,
	{
		isCustomChannel,
		isEmailChannel,
		isLetterChannel,
		channelTypeId,
		customLabel,
		title,
		body,
		dueAt,
		templateMode,
		selectedEmailTemplateId,
		selectedPostMailingTemplateId,
		selectedContactId,
	},
) {
	const useEmailTemplate = isEmailChannel && templateMode === 'template' && selectedEmailTemplateId;
	const usePostTemplate = isLetterChannel && templateMode === 'template' && selectedPostMailingTemplateId;
	return {
		...base,
		activityType: usePostTemplate || useEmailTemplate ? 'communication' : 'task',
		channelTypeId: channelTypeId || null,
		customActivityLabel: isCustomChannel ? customLabel || null : null,
		title: title.trim(),
		body: body || null,
		status: 'planned',
		dueAt: dueAt ? new Date(dueAt).toISOString() : null,
		...(useEmailTemplate && { emailTemplateId: selectedEmailTemplateId }),
		...(usePostTemplate && { postMailingTemplateId: selectedPostMailingTemplateId }),
		...((useEmailTemplate || usePostTemplate) && selectedContactId && { crmContactId: selectedContactId }),
	};
}

/**
 * Encapsulates all state, derived values, effects, and submit logic for NewActivityModal.
 *
 * @param {Object} params
 * @param {string} [params.prefillCompanyId]
 * @param {string} [params.prefillCompanyName]
 * @param {string} [params.prefillDealId]
 * @param {string} [params.prefillDealTitle]
 * @param {Function} params.onClose
 * @returns {Object}
 */
export function useNewActivityForm({ prefillCompanyId, prefillCompanyName, prefillDealId, prefillDealTitle, onClose }) {
	const [mode, setMode] = useState('todo');

	const [companyInput, setCompanyInput] = useState(prefillCompanyName ?? '');
	const [selectedCompany, setSelectedCompany] = useState(
		prefillCompanyId ? { id: prefillCompanyId, companyName: prefillCompanyName ?? '' } : null,
	);
	const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

	const [dealInput, setDealInput] = useState(prefillDealTitle ?? '');
	const [selectedDeal, setSelectedDeal] = useState(
		prefillDealId ? { id: prefillDealId, title: prefillDealTitle ?? '' } : null,
	);
	const [showDealDropdown, setShowDealDropdown] = useState(false);

	const [channelTypeId, setChannelTypeId] = useState('');
	const [customLabel, setCustomLabel] = useState('');
	const [title, setTitle] = useState('');
	const [body, setBody] = useState('');
	const [dueAt, setDueAt] = useState('');
	const [templateMode, setTemplateMode] = useState('manual');
	const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
	const [selectedPostMailingTemplateId, setSelectedPostMailingTemplateId] = useState('');
	const [selectedContactId, setSelectedContactId] = useState('');

	const companyDropdownRef = useRef(null);
	const dealDropdownRef = useRef(null);
	const createActivity = useCreateActivity();
	const showError = useErrorStore((s) => s.showError);

	const { data: channelTypes = [] } = useChannelTypes(false);
	const activeChannels = channelTypes.filter((c) => c.orgEnabled !== false);
	const selectedChannel = activeChannels.find((c) => c.id === channelTypeId);
	const isCustomChannel = selectedChannel?.slug === 'manual_custom';
	const isEmailChannel = selectedChannel?.slug === 'email';
	const isLetterChannel = selectedChannel?.slug === 'letter' || selectedChannel?.slug === 'post_mailing';
	const showTemplateOption = mode === 'todo' && (isEmailChannel || isLetterChannel);
	const isTemplateMode = showTemplateOption && templateMode === 'template';

	const { data: emailTemplates = [] } = useEmailTemplates();
	const { data: postMailingTemplates = [] } = usePostMailingTemplates();

	const resolvedCompanyId = selectedCompany?.id ?? selectedDeal?.crmCompanyId ?? null;
	const { data: companyDetailData } = useCompanyDetail(isTemplateMode ? resolvedCompanyId : null);
	const companyContacts = useMemo(() => companyDetailData?.contacts ?? [], [companyDetailData?.contacts]);
	const eligibleContacts = useMemo(() => {
		if (isEmailChannel) return companyContacts.filter((c) => c.emailAddress);
		return companyContacts;
	}, [companyContacts, isEmailChannel]);

	const showCompanyPicker = !prefillCompanyId && !prefillDealId;

	const { results: companyResults, loading: companySearchLoading } = useCompanySearch(
		showCompanyPicker && !selectedCompany ? companyInput : '',
	);
	const { results: dealResults, loading: dealSearchLoading } = useDealSearch(
		selectedDeal ? '' : dealInput,
		resolvedCompanyId,
	);

	// Set default channel
	if (mode !== 'note' && !channelTypeId && activeChannels.length > 0) {
		setChannelTypeId(activeChannels[0].id);
	}

	useEffect(() => {
		if (!showCompanyDropdown && !showDealDropdown) return;
		const handler = (e) => {
			if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target))
				setShowCompanyDropdown(false);
			if (dealDropdownRef.current && !dealDropdownRef.current.contains(e.target)) setShowDealDropdown(false);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [showCompanyDropdown, showDealDropdown]);

	useEffect(() => {
		if (companyResults.length > 0 && !selectedCompany) setShowCompanyDropdown(true);
	}, [companyResults, selectedCompany]);

	useEffect(() => {
		if (dealResults.length > 0 && !selectedDeal) setShowDealDropdown(true);
	}, [dealResults, selectedDeal]);

	useEffect(() => {
		if (!isTemplateMode || eligibleContacts.length === 0) {
			setSelectedContactId('');
			return;
		}
		const primary = eligibleContacts.find((c) => c.isPrimary);
		setSelectedContactId(primary?.id ?? (eligibleContacts.length === 1 ? eligibleContacts[0].id : ''));
	}, [isTemplateMode, eligibleContacts]);

	const handleCompanyInputChange = useCallback(
		(e) => {
			setCompanyInput(e.target.value);
			if (selectedCompany) {
				setSelectedCompany(null);
				setSelectedDeal(null);
				setDealInput('');
			}
		},
		[selectedCompany],
	);

	const handleSelectCompany = (company) => {
		setSelectedCompany(company);
		setCompanyInput(company.companyName);
		setShowCompanyDropdown(false);
	};

	const handleClearCompany = () => {
		setSelectedCompany(null);
		setCompanyInput('');
		setSelectedDeal(null);
		setDealInput('');
	};

	const handleDealInputChange = useCallback(
		(e) => {
			setDealInput(e.target.value);
			if (selectedDeal && !prefillDealId) setSelectedDeal(null);
		},
		[selectedDeal, prefillDealId],
	);

	const handleSelectDeal = (deal) => {
		setSelectedDeal(deal);
		setDealInput(deal.title);
		setShowDealDropdown(false);
		if (!selectedCompany && !prefillCompanyId && deal.crmCompanyId) {
			setSelectedCompany({
				id: deal.crmCompanyId,
				companyName: deal.companyNameSnapshot ?? '',
			});
			setCompanyInput(deal.companyNameSnapshot ?? '');
		}
	};

	const handleClearDeal = () => {
		setSelectedDeal(null);
		setDealInput('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!resolvedCompanyId) return;
		if (mode !== 'note' && !title.trim()) return;
		if (mode === 'note' && !body.trim()) return;

		const base = {
			crmCompanyId: resolvedCompanyId,
			...(selectedDeal && { dealId: selectedDeal.id }),
		};

		let payload;
		if (mode === 'note') {
			payload = buildNotePayload(base, body);
		} else if (mode === 'log') {
			payload = buildLogPayload(base, { isCustomChannel, channelTypeId, customLabel, title, body });
		} else {
			payload = buildTodoPayload(base, {
				isCustomChannel,
				isEmailChannel,
				isLetterChannel,
				channelTypeId,
				customLabel,
				title,
				body,
				dueAt,
				templateMode,
				selectedEmailTemplateId,
				selectedPostMailingTemplateId,
				selectedContactId,
			});
		}

		try {
			await createActivity.mutateAsync(payload);
			onClose();
		} catch (err) {
			showError({ error: err, title: 'Aktivität konnte nicht gespeichert werden' });
		}
	};

	return {
		mode,
		setMode,
		companyInput,
		selectedCompany,
		showCompanyDropdown,
		setShowCompanyDropdown,
		companyDropdownRef,
		companyResults,
		companySearchLoading,
		dealInput,
		selectedDeal,
		showDealDropdown,
		setShowDealDropdown,
		dealDropdownRef,
		dealResults,
		dealSearchLoading,
		channelTypeId,
		setChannelTypeId,
		customLabel,
		setCustomLabel,
		title,
		setTitle,
		body,
		setBody,
		dueAt,
		setDueAt,
		templateMode,
		setTemplateMode,
		selectedEmailTemplateId,
		setSelectedEmailTemplateId,
		selectedPostMailingTemplateId,
		setSelectedPostMailingTemplateId,
		selectedContactId,
		setSelectedContactId,
		activeChannels,
		isCustomChannel,
		isEmailChannel,
		isLetterChannel,
		showTemplateOption,
		isTemplateMode,
		emailTemplates,
		postMailingTemplates,
		resolvedCompanyId,
		eligibleContacts,
		showCompanyPicker,
		handleCompanyInputChange,
		handleSelectCompany,
		handleClearCompany,
		handleDealInputChange,
		handleSelectDeal,
		handleClearDeal,
		handleSubmit,
		createActivity,
	};
}
